interface JsonApiError {
  detail?: string;
  title?: string;
  source?: { pointer?: string };
}

interface JsonApiBody {
  errors?: JsonApiError[];
}

/**
 * Turns a JSON:API `source.pointer` (e.g. "/data/attributes/postal_code") into
 * a human-readable field label (e.g. "Postal Code"). Returns undefined when
 * there's no usable, field-specific pointer (e.g. a top-level "base" error).
 */
function fieldLabelFromPointer(pointer?: string): string | undefined {
  const segment = pointer?.split('/').filter(Boolean).pop();
  if (!segment || segment === 'base') return undefined;

  return segment
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Builds "Field Name: detail" when the error points at a field, else "detail". */
function formatJsonApiError(error: JsonApiError): string | undefined {
  const text = error?.detail || error?.title;
  if (!text) return undefined;

  const field = fieldLabelFromPointer(error?.source?.pointer);
  return field ? `${field}: ${text}` : text;
}

interface ApolloLikeError {
  graphQLErrors?: { message?: string }[];
  message?: string;
}

/**
 * Pulls the most specific raw error string out of an error value. Prefers the
 * first GraphQL error's message — matching the existing repo convention of
 * reading structured `graphQLErrors` off the ApolloError (see
 * DeactivateAccountModal / DeleteDepotModal) — then falls back to the error's
 * own `message`, then to the value itself when it's already a string.
 */
function extractRawMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;

  const { graphQLErrors, message } = error as ApolloLikeError;

  const graphQLMessage = graphQLErrors?.[0]?.message;
  if (graphQLMessage) return graphQLMessage;

  return typeof message === 'string' ? message : undefined;
}

/**
 * Finds `marker` in the string and parses the first balanced `{…}` object that
 * follows it. Brace-counting (rather than a regex) so nested objects in the
 * body don't truncate the match.
 */
function extractJsonObjectAfter(
  message: string,
  marker: string,
): JsonApiBody | undefined {
  const markerIndex = message.indexOf(marker);
  if (markerIndex === -1) return undefined;

  const start = message.indexOf('{', markerIndex);
  if (start === -1) return undefined;

  let depth = 0;
  for (let i = start; i < message.length; i += 1) {
    const char = message[i];

    if (char === '{') depth += 1;
    else if (char === '}') {
      depth -= 1;

      if (depth === 0) {
        try {
          return JSON.parse(message.slice(start, i + 1));
        } catch {
          return undefined;
        }
      }
    }
  }

  return undefined;
}

/**
 * Backend/network errors often reach the client as a serialized HTTP exception,
 * e.g.:
 *
 *   Exception status=400 patch https://… body={"errors":[{"title":"…",
 *   "detail":"Field length can not exceed 20 characters",…}]} headers={…}
 *
 * Showing that raw string to a user is noise. This reads the most specific
 * message off the error (preferring structured `graphQLErrors`) and pulls the
 * human-readable text out of any embedded JSON:API `body` (preferring `detail`,
 * then `title`).
 *
 * Returns:
 *  - the joined error detail(s) when a JSON:API body can be parsed,
 *  - `undefined` when there's no usable message, or it's a serialized exception
 *    we can't extract anything from (so callers fall back to a generic message
 *    instead of surfacing the noise),
 *  - the original string otherwise (it may already be a short, readable
 *    message such as "Network error").
 */
export default function parseBackendErrorMessage(
  error?: unknown,
): string | undefined {
  const message = extractRawMessage(error);
  if (!message) return undefined;

  const body = extractJsonObjectAfter(message, 'body=');

  if (body && Array.isArray(body.errors)) {
    const details = body.errors
      .map(formatJsonApiError)
      .filter((detail): detail is string => Boolean(detail));

    if (details.length) return details.join('\n');
  }

  // It's a serialized exception but we couldn't extract anything useful —
  // don't surface the raw noise.
  if (/\bbody=\{/.test(message) || /^Exception\b/.test(message)) {
    return undefined;
  }

  return message;
}
